import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { PartnerPublicCard } from '../../shared/PartnerPublicCard';
import { PARTNER_PUBLIC_CARD_CSS } from '../../shared/partnerPublicCardStyles';
import { buildMockPartner, usesPremiumGridForTier, tierSlug } from '../../../utils/partnerPublicDisplay';

const NEON = '#38BDF8';
const SURFACE_UP = '#0f1f38';
const SURFACE_MID = '#0a1628';

/**
 * Live preview of one public partner card for sponsor tier settings.
 */
export default function PartnerPublicPreview({ tierDraft, compact = false }) {
  const slug = tierSlug({ sponsor_tier: { tier_name: tierDraft?.tier_name || '' } });
  const defaultLayout = usesPremiumGridForTier(slug) ? 'premium' : 'regular';
  const [layout, setLayout] = useState(defaultLayout);

  useEffect(() => {
    setLayout(defaultLayout);
  }, [defaultLayout]);

  const mockPartner = useMemo(() => buildMockPartner(tierDraft), [
    tierDraft?.tier_name,
    tierDraft?.logo_size,
    tierDraft?.is_show_description,
    tierDraft?.is_show_contact,
  ]);

  const handleLayout = (_, value) => {
    if (value) setLayout(value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: compact ? 'auto' : '100%',
        position: compact ? 'relative' : { lg: 'sticky' },
        top: compact ? 0 : { lg: 24 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.8rem',
            fontWeight: 600,
            color: alpha(NEON, 0.75),
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <VisibilityIcon sx={{ fontSize: 16, color: alpha(NEON, 0.5) }} />
          Public page preview
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={layout}
          exclusive
          onChange={handleLayout}
          sx={{
            '& .MuiToggleButton-root': {
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.72rem',
              textTransform: 'none',
              color: alpha('#E2E8F0', 0.65),
              borderColor: alpha(NEON, 0.35),
              px: 1.25,
              py: 0.35,
              '&.Mui-selected': {
                color: NEON,
                background: alpha(NEON, 0.12),
              },
            },
          }}
        >
          <ToggleButton value="premium">Premium card</ToggleButton>
          <ToggleButton value="regular">Regular card</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Typography
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.72rem',
          color: alpha('#94A3B8', 0.9),
          mb: 1.5,
          lineHeight: 1.5,
        }}
      >
        Sample partner card as it may appear on the public sponsors page. Changes apply before you save.
      </Typography>

      <Box
        className="partner-public-card-scope preview-mode"
        sx={{
          flex: 1,
          borderRadius: '12px',
          border: `1px solid ${alpha(NEON, 0.2)}`,
          background: '#060608',
          p: { xs: 1.5, sm: 2 },
          overflow: 'auto',
          minHeight: compact ? 280 : 360,
        }}
      >
        <style>{PARTNER_PUBLIC_CARD_CSS}</style>
        <Box
          sx={{
            transform: compact ? 'scale(0.92)' : 'scale(0.88)',
            transformOrigin: 'top center',
            maxWidth: layout === 'premium' ? 440 : 320,
            mx: 'auto',
          }}
        >
          <PartnerPublicCard partner={mockPartner} layout={layout} staticPreview />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: '8px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: alpha(SURFACE_UP, 0.5),
        }}
      >
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color: alpha(NEON, 0.45) }}>
          // PREVIEW_SAMPLE_DATA
        </Typography>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', color: alpha('#94A3B8', 0.85), mt: 0.25 }}>
          Real partners use company logos, covers, and partnership records from Partner Management.
        </Typography>
      </Box>
    </Box>
  );
}
